// Benutzerverwaltung und Rollenwechsel (DCP-2228 Folge-Feature)
//
// Getrennt vom event-service, weil das hier nichts mit Veranstaltungen zu
// tun hat: es geht um Zugänge zur gesamten Anwendung.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ManagedUser, PermissionCatalog, Role } from '@/types';

const USERS_KEY = 'users';
const CATALOG_KEY = 'permission-catalog';

export function useUsers() {
  return useQuery<ManagedUser[]>({
    queryKey: [USERS_KEY],
    queryFn: () => apiClient.getUsers(),
    // Kürzer als bei Referenzdaten: wer gerade Rechte vergibt, will das Ergebnis
    // sofort sehen, nicht erst nach Minuten.
    staleTime: 30_000,
    retry: false,
  });
}

export function usePermissionCatalog() {
  return useQuery<PermissionCatalog>({
    queryKey: [CATALOG_KEY],
    queryFn: () => apiClient.getPermissionCatalog(),
    // Ändert sich nur mit einem Deployment.
    staleTime: 60 * 60_000,
  });
}

export function useSaveUser() {
  const qc = useQueryClient();

  return useMutation<ManagedUser, Error, ManagedUser>({
    mutationFn: (user) => apiClient.saveUser(user),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [USERS_KEY] });
      // Auch die eigene Kennung neu laden: wer sich selbst bearbeitet, sieht sonst
      // veraltete Rechte, bis der /api/me-Cache abläuft.
      qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (email) => apiClient.deleteUser(email),
    onSuccess: () => qc.invalidateQueries({ queryKey: [USERS_KEY] }),
  });
}

/**
 * Wechselt die Rolle zum Testen (nur Staging).
 *
 * Leert danach den gesamten Cache, nicht nur einzelne Schlüssel: nach einem Wechsel
 * ist praktisch jede zwischengespeicherte Antwort aus Sicht der neuen Rolle falsch —
 * Veranstaltungslisten, Depots, Rechte. Alles neu zu laden ist hier richtiger als
 * einzelne Schlüssel zu erraten.
 */
export function useSwitchRole() {
  const qc = useQueryClient();

  return useMutation<unknown, Error, { role: Role; depots: string[] }>({
    mutationFn: ({ role, depots }) => apiClient.switchRole(role, depots),
    onSuccess: () => qc.clear(),
  });
}
